import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel, Tooltip, TextField, CircularProgress } from '@mui/material';
import { InfoOutlinedIcon, InsertPhotoOutlinedIcon } from 'icons/materialIcons';

const TeamV2EmailSignatureContainer = ({ memberData, setMemberData, isNewMember = false }) => {
    const [loading, setLoading] = useState(false);
    const [includeBanner, setIncludeBanner] = useState(false);
    const [includeQRCode, setIncludeQRCode] = useState(true);
    const fileInputRef = useRef(null);

    // Mock email signature data
    const [emailSignatureData, setEmailSignatureData] = useState({
        name: memberData.firstName && memberData.lastName ? `${memberData.firstName} ${memberData.lastName}` : '',
        jobTitle: memberData.jobTitle || '',
        company: memberData.company || '',
        phoneNumber: memberData.phone || '',
        location: memberData.location || '',
        disclaimer: '',
        bannerImageUrl: '',
        profilePictureUrl: memberData.profilePictureUrl || '',
        companyLogoUrl: '',
        qrCodeLogoUrl: ''
    });

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setEmailSignatureData(prev => ({
                ...prev,
                bannerImageUrl: url
            }));
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setEmailSignatureData(prev => ({
                ...prev,
                bannerImageUrl: url
            }));
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const handleIncludeBannerChange = (event) => {
        const { checked } = event.target;
        setIncludeBanner(checked);
    };

    const handleIncludeQRCodeChange = (event) => {
        const { checked } = event.target;
        setIncludeQRCode(checked);
    };

    const handleInputChange = (field, value) => {
        setEmailSignatureData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Box my={4}>
            <Typography variant="body2"
                sx={{
                    fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px', paddingLeft: 2
                }}
                gutterBottom align="left">
                Email Signature
            </Typography>
            {loading ? (
                <CircularProgress />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2,
                        maxWidth: 800,
                        margin: 'auto',
                    }}
                >
                    {/* Profile Picture, Company Logo, QR Code Row */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '120px',
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer'
                                }}
                                onClick={handleClick}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#999', marginBottom: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                        Profile Picture
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '120px',
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer'
                                }}
                                onClick={handleClick}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#999', marginBottom: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                        Company Logo
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '120px',
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer'
                                }}
                                onClick={handleClick}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#999', marginBottom: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                        QR Code Logo
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}></Grid>
                    </Grid>

                    {/* Include Banner Checkbox */}
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeBanner}
                                    onChange={handleIncludeBannerChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                                    Include Banner
                                </Typography>
                            }
                        />
                        <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.6:1)" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#4f4f4f' }} />
                        </Tooltip>
                    </Box>

                    {/* Banner Image Upload Area */}
                    <Box
                        sx={{
                            width: '100%',
                            height: '200px',
                            backgroundColor: '#f7f7f7',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed #e0e0e0',
                            cursor: 'pointer',
                            marginTop: 2
                        }}
                        onClick={handleClick}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#999', marginBottom: 1 }} />
                            <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                                Banner Image Upload Area
                            </Typography>
                        </Box>
                    </Box>

                    {/* Form Fields */}
                    <Grid container spacing={2} sx={{ marginTop: 1 }}>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                Name
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                                Job Title
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.jobTitle}
                                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                                Company
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.company}
                                onChange={(e) => handleInputChange('company', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                                Phone Number
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.phoneNumber}
                                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                                Location
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                                Disclaimer
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={emailSignatureData.disclaimer}
                                onChange={(e) => handleInputChange('disclaimer', e.target.value)}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>

                    {/* Include QR Code Checkbox */}
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeQRCode}
                                    onChange={handleIncludeQRCodeChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                                    Include QRCode
                                </Typography>
                            }
                        />
                    </Box>

                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept="image/*"
                    />
                </Box>
            )}
        </Box>
    );
};

export default TeamV2EmailSignatureContainer;
